import { useMemo, useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { useAppLanguage } from '../i18n'
import type { LlmModelOption } from '../types/llmModel'

const PROVIDER_ORDER = ['ollama', 'openai', 'anthropic'] as const

type ProviderTab = (typeof PROVIDER_ORDER)[number]

function providerLabel(
  p: ProviderTab,
  t: {
    tabOllama: string
    tabOpenai: string
    tabAnthropic: string
  }
): string {
  if (p === 'ollama') return t.tabOllama
  if (p === 'openai') return t.tabOpenai
  return t.tabAnthropic
}

export type AgentModelPickerModalProps = {
  open: boolean
  onClose: () => void
  models: LlmModelOption[]
  value: string
  onApply: (modelId: string) => void
  loading?: boolean
  defaultOptionLabel: string
}

export default function AgentModelPickerModal({
  open,
  onClose,
  models,
  value,
  onApply,
  loading,
  defaultOptionLabel,
}: AgentModelPickerModalProps) {
  const { t } = useAppLanguage()
  const mp = t.agents.modelPicker
  const [tab, setTab] = useState<ProviderTab>('ollama')
  const [search, setSearch] = useState('')
  const [draft, setDraft] = useState(value)

  useEffect(() => {
    if (!open) return
    setDraft(value)
    setSearch('')
    const byProv = new Set(models.map(m => (m.provider || 'ollama').toLowerCase()))
    if (value) {
      const prov = (models.find(m => m.name === value)?.provider || 'ollama').toLowerCase()
      if (PROVIDER_ORDER.includes(prov as ProviderTab) && byProv.has(prov)) {
        setTab(prov as ProviderTab)
        return
      }
    }
    const found = PROVIDER_ORDER.find(p => byProv.has(p))
    setTab(found || 'ollama')
  }, [open, value, models])

  const byProvider = useMemo(() => {
    const map = new Map<string, LlmModelOption[]>()
    for (const m of models) {
      const p = (m.provider || 'ollama').toLowerCase()
      if (!map.has(p)) map.set(p, [])
      map.get(p)!.push(m)
    }
    return map
  }, [models])

  const filtered = useMemo(() => {
    const list = byProvider.get(tab) || []
    const q = search.trim().toLowerCase()
    if (!q) return list
    return list.filter(
      m =>
        m.name.toLowerCase().includes(q) ||
        (m.description && m.description.toLowerCase().includes(q))
    )
  }, [byProvider, tab, search])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-stretch justify-center p-0 md:items-center md:p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-label={mp.cancel}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="agent-model-picker-title"
        className="relative z-10 flex h-[100dvh] w-screen flex-col overflow-hidden bg-white shadow-2xl dark:bg-zinc-950 md:h-[92vh] md:max-h-[920px] md:w-[min(96vw,1320px)] md:rounded-3xl md:border md:border-emerald-200/60 dark:md:border-emerald-900/50"
      >
        <header className="flex items-center justify-between border-b border-zinc-200/80 px-4 py-3 md:px-6 md:py-4 dark:border-zinc-800">
          <div className="min-w-0">
            <h2 id="agent-model-picker-title" className="truncate text-base font-semibold text-zinc-900 md:text-xl dark:text-white">
              {mp.title}
            </h2>
            <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
              {(byProvider.get(tab) || []).length} modèles
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-zinc-500 transition hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="border-b border-zinc-200/80 px-4 py-3 md:px-6 dark:border-zinc-800">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {PROVIDER_ORDER.map(p => {
              const count = (byProvider.get(p) || []).length
              if (count === 0) return null
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setTab(p)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-semibold transition md:text-sm ${
                    tab === p
                      ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/30'
                      : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700'
                  }`}
                >
                  {providerLabel(p, mp)}
                  <span className="ml-2 inline-flex rounded-full bg-black/10 px-2 py-0.5 text-[10px] font-bold md:text-xs">
                    {count}
                  </span>
                </button>
              )
            })}
          </div>
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={mp.search}
            className="w-full rounded-2xl border border-emerald-100/80 bg-zinc-50 px-4 py-2.5 text-sm text-zinc-800 placeholder:text-zinc-400 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/50 dark:bg-zinc-900 dark:text-zinc-100"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 md:px-6 md:py-4">
          {loading ? (
            <p className="px-2 py-8 text-center text-sm text-zinc-500">{mp.loading}</p>
          ) : filtered.length === 0 ? (
            <p className="px-2 py-8 text-center text-sm text-zinc-500">
              {search.trim() ? mp.noMatch : mp.emptyTab}
            </p>
          ) : (
            <ul className="grid grid-cols-1 gap-2 md:gap-3 lg:grid-cols-2 xl:grid-cols-3">
              {filtered.map(m => {
                const selected = draft === m.name
                return (
                  <li key={`${m.provider}:${m.name}`}>
                    <button
                      type="button"
                      onClick={() => setDraft(m.name)}
                      className={`flex w-full flex-col rounded-2xl border px-4 py-3 text-left text-sm transition ${
                        selected
                          ? 'border-emerald-500 bg-emerald-50/90 shadow-sm dark:border-emerald-600 dark:bg-emerald-950/40'
                          : 'border-zinc-200/80 hover:border-emerald-200/80 hover:bg-zinc-50 dark:border-zinc-800 dark:hover:border-emerald-900/50 dark:hover:bg-zinc-900/60'
                      }`}
                    >
                      <span className="flex items-center justify-between gap-3 font-medium text-zinc-900 dark:text-zinc-100">
                        <span className="truncate">{m.name}</span>
                        <span
                          className={`h-3 w-3 rounded-full transition ${
                            selected ? 'bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.2)]' : 'bg-zinc-300 dark:bg-zinc-700'
                          }`}
                        />
                      </span>
                      {m.description ? (
                        <span className="mt-0.5 line-clamp-2 text-xs text-zinc-500 dark:text-zinc-400">
                          {m.description}
                        </span>
                      ) : null}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <footer className="flex flex-col gap-3 border-t border-zinc-200/80 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:flex-row md:items-center md:justify-between md:px-6 md:py-4 dark:border-zinc-800">
          <button
            type="button"
            onClick={() => {
              onApply('')
              onClose()
            }}
            className="rounded-full px-4 py-2 text-left text-sm font-medium text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
          >
            {defaultOptionLabel}
          </button>
          <div className="flex w-full gap-2 md:w-auto">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-full border border-zinc-200 px-4 py-2 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-50 md:flex-none dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
            >
              {mp.cancel}
            </button>
            <button
              type="button"
              onClick={() => {
                onApply(draft)
                onClose()
              }}
              className="flex-1 rounded-full bg-emerald-500 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-600 md:flex-none"
            >
              {mp.apply}
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}
